import type { Refill, Site, Tank } from "@/api/types";

/**
 * "What is about to go wrong."
 *
 * An operations manager does not open this product to admire a total. They open it to find
 * out which of their sites is going to embarrass them today. Everything here answers that
 * one question, and the dashboard leads with the answer.
 *
 * Pure, and derived from data the spec already returns — there is no `/v1/alerts` endpoint,
 * so the client computes this from tanks, sites and the refill ledger.
 */

export type RiskLevel = "dry" | "low" | "overdue" | "ok";

export interface TankRisk {
  tank: Tank;
  site: Site;
  level: RiskLevel;
  /** Percentage full, 0–100. Null when the tank has never been refilled or read. */
  percent: number | null;
  /** Estimated liters burned per day at this tank, from its own history. Null if unknowable. */
  dailyBurnLiters: number | null;
  /** Days until the tank is empty at the current burn rate. Null when the burn is unknown. */
  daysToDry: number | null;
  /** Days since fuel was last added at this tank's site. Null when never refilled. */
  daysSinceRefill: number | null;
  /** True when the site has gone longer than the org's `refill_overdue_days` without fuel. */
  overdue: boolean;
}

const MS_PER_DAY = 86_400_000;

function daysBetween(a: Date, b: Date): number {
  return (b.getTime() - a.getTime()) / MS_PER_DAY;
}

/**
 * Liters burned per day, derived from consecutive refills at the same tank.
 *
 * Consumption between two refills is `previous.after_liters − next.before_liters` — the
 * fuel that vanished from the tank between one visit and the next. That is exactly how the
 * spec defines `liters_consumed` on ReportTotals, and using the same formula means the
 * dashboard and the report can never tell the operator two different stories.
 *
 * `null` when there are fewer than two completed refills: with one data point there is no
 * rate, and inventing one ("assume 500 L/day") would be a lie the operator would act on.
 */
export function estimateDailyBurn(refills: Refill[]): number | null {
  const completed = refills
    .filter((r) => r.status === "completed")
    .sort(
      (a, b) => new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime(),
    );

  if (completed.length < 2) return null;

  let litersConsumed = 0;
  let days = 0;

  for (let i = 0; i < completed.length - 1; i++) {
    const previous = completed[i];
    const next = completed[i + 1];
    if (!previous || !next) continue;

    const consumed = previous.after_liters - next.before_liters;
    const span = daysBetween(new Date(previous.recorded_at), new Date(next.recorded_at));

    // A negative consumption means fuel appeared without a recorded refill, and a zero-length
    // span means two refills share a timestamp. Both are real data-quality problems, but
    // neither is a burn rate — skip the pair rather than poison the average.
    if (consumed <= 0 || span <= 0) continue;

    litersConsumed += consumed;
    days += span;
  }

  if (days <= 0 || litersConsumed <= 0) return null;
  return litersConsumed / days;
}

export interface RiskInputs {
  sites: Site[];
  /** Tanks keyed by their site id. */
  tanksBySite: Map<string, Tank[]>;
  /** Every refill in the window, keyed by tank id. */
  refillsByTank: Map<string, Refill[]>;
  /** The org-wide default, used by any tank that does not override it. */
  orgLowThresholdPercent: number;
  orgRefillOverdueDays: number;
  now: Date;
}

/**
 * A tank is `dry` when it will be empty within DRY_DAYS at its own measured burn rate.
 * Three days is not arbitrary: it is roughly the lead time to get a fuel truck to a site,
 * so it is the last moment at which the operator can still act rather than react.
 */
const DRY_DAYS = 3;

export function assessTanks(input: RiskInputs): TankRisk[] {
  const risks: TankRisk[] = [];

  for (const site of input.sites) {
    const tanks = input.tanksBySite.get(site.id) ?? [];

    const daysSinceRefill = site.last_refill_at
      ? daysBetween(new Date(site.last_refill_at), input.now)
      : null;

    const overdue =
      daysSinceRefill === null
        ? false // never refilled is a different problem from overdue, and is shown as such
        : daysSinceRefill > input.orgRefillOverdueDays;

    for (const tank of tanks) {
      if (tank.status !== "active") continue;

      const percent = tank.level_percent ?? null;
      const threshold = tank.low_level_threshold_percent ?? input.orgLowThresholdPercent;

      const dailyBurnLiters = estimateDailyBurn(input.refillsByTank.get(tank.id) ?? []);

      const daysToDry =
        dailyBurnLiters !== null &&
        dailyBurnLiters > 0 &&
        tank.current_level_liters !== undefined
          ? tank.current_level_liters / dailyBurnLiters
          : null;

      let level: RiskLevel = "ok";
      if (daysToDry !== null && daysToDry <= DRY_DAYS) {
        level = "dry";
      } else if (percent !== null && percent <= threshold) {
        level = "low";
      } else if (overdue) {
        level = "overdue";
      }

      risks.push({
        tank,
        site,
        level,
        percent,
        dailyBurnLiters,
        daysToDry,
        daysSinceRefill,
        overdue,
      });
    }
  }

  return risks;
}

const ORDER: Record<RiskLevel, number> = { dry: 0, low: 1, overdue: 2, ok: 3 };

/** Worst first, and within a level, the one that runs out soonest. Nothing else sorts. */
export function sortByUrgency(risks: TankRisk[]): TankRisk[] {
  return [...risks].sort((a, b) => {
    if (ORDER[a.level] !== ORDER[b.level]) return ORDER[a.level] - ORDER[b.level];
    if (a.daysToDry !== null && b.daysToDry !== null) return a.daysToDry - b.daysToDry;
    if (a.daysToDry !== null) return -1;
    if (b.daysToDry !== null) return 1;
    return (b.percent ?? 101) - (a.percent ?? 101) > 0 ? -1 : 1;
  });
}

export const needsAttention = (r: TankRisk): boolean => r.level !== "ok";
